import React, { Component } from "react";
import {
  TextField,
  Box,
  Typography,
  Paper,
  CircularProgress,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import ScheduleIcon from "@material-ui/icons/Schedule";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point } from "../../utilities/turfHelpers";
import { isDeliveryAreaOpen } from "../../utilities/businessHoursValidation";

const styles = (theme) => ({
  root: {
    position: "relative",
  },
  validationMessage: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: "8px",
    fontSize: "0.9rem",
  },
  validAddress: {
    background: "#e8f5e9",
    color: "#2e7d32",
  },
  invalidAddress: {
    background: "#ffebee",
    color: "#c62828",
  },
  closedAddress: {
    background: "#fff3e0",
    color: "#e65100",
  },
  scheduleInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    fontSize: "0.85rem",
    opacity: 0.9,
  },
  suggestionsPaper: {
    position: "absolute",
    zIndex: 1000,
    marginTop: theme.spacing(0.5),
    maxHeight: "300px",
    overflow: "auto",
    width: "100%",
  },
  suggestionItem: {
    padding: theme.spacing(1.5),
    cursor: "pointer",
    borderBottom: "1px solid #f0f0f0",
    "&:hover": {
      background: "#f5f5f5",
    },
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
});

class AddressAutocomplete extends Component {
  constructor(props) {
    super(props);

    this.state = {
      inputValue: "",
      suggestions: [],
      loading: false,
      showSuggestions: false,
      selectedAddress: null,
      validationStatus: null, // null | 'valid' | 'invalid' | 'closed'
      validationMessage: "",
      scheduleMessage: "", // Mensagem sobre horário de funcionamento
      deliveryAreas: [],
      boundingBox: null, // Bounding box calculado das áreas de entrega
    };

    this.debounceTimeout = null;
  }

  componentDidMount() {
    this.loadDeliveryAreas();
  }

  componentWillUnmount() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }

  loadDeliveryAreas = async () => {
    const { firebase, organizationId } = this.props;

    try {
      const snapshot = await firebase
        .deliveryAreas(organizationId)
        .orderByChild("active")
        .equalTo(true)
        .once("value");

      const areasData = snapshot.val() || {};
      const areasArray = Object.keys(areasData).map((id) => ({
        id,
        ...areasData[id],
      }));

      // Calcula o bounding box de todas as áreas de entrega
      const boundingBox = this.calculateBoundingBox(areasArray);

      this.setState({ deliveryAreas: areasArray, boundingBox });
    } catch (error) {
      console.error("Error loading delivery areas:", error);
    }
  };

  // Calcula o bounding box (retângulo) que engloba todas as áreas de entrega
  calculateBoundingBox = (areas) => {
    if (!areas || areas.length === 0) {
      return null;
    }

    let minLon = Infinity;
    let maxLon = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;

    areas.forEach((area) => {
      if (area.geojson && area.geojson.geometry && area.geojson.geometry.coordinates) {
        // Polígono tem coordenadas em formato [[lon, lat], [lon, lat], ...]
        const coordinates = area.geojson.geometry.coordinates[0];
        coordinates.forEach(([lon, lat]) => {
          if (lon < minLon) minLon = lon;
          if (lon > maxLon) maxLon = lon;
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
        });
      }
    });

    // Verifica se encontrou coordenadas válidas
    if (minLon === Infinity || maxLon === -Infinity) {
      return null;
    }

    // Adiciona uma pequena margem (aproximadamente 1km) para incluir endereços próximos às bordas
    const margin = 0.01; // ~1km em graus
    return {
      minLon: minLon - margin,
      maxLon: maxLon + margin,
      minLat: minLat - margin,
      maxLat: maxLat + margin,
      // Centro para o bias de proximidade
      centerLon: (minLon + maxLon) / 2,
      centerLat: (minLat + maxLat) / 2,
    };
  };

  handleInputChange = (e) => {
    const value = e.target.value;
    this.setState({
      inputValue: value,
      showSuggestions: true,
      validationStatus: null,
    });

    // Clear previous timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Debounce API call
    if (value.length >= 3) {
      this.setState({ loading: true });
      this.debounceTimeout = setTimeout(() => {
        this.fetchSuggestions(value);
      }, 500);
    } else {
      this.setState({ suggestions: [], loading: false });
    }
  };

  fetchSuggestions = async (query) => {
    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;

    if (!apiKey) {
      console.error("Geoapify API key not configured");
      this.setState({ loading: false });
      return;
    }

    try {
      const { boundingBox } = this.state;
      
      // Monta os parâmetros de filtro baseado nas áreas de entrega cadastradas
      let filterParams = "filter=countrycode:br";
      let biasParams = "bias=proximity:-38.5434,-3.7172"; // Fortaleza como fallback
      
      if (boundingBox) {
        // Usa o bounding box das áreas de entrega para limitar resultados
        // Formato: rect:lon1,lat1,lon2,lat2 (sudoeste, nordeste)
        filterParams = `filter=rect:${boundingBox.minLon},${boundingBox.minLat},${boundingBox.maxLon},${boundingBox.maxLat}`;
        // Usa o centro das áreas como ponto de referência para ordenar por proximidade
        biasParams = `bias=proximity:${boundingBox.centerLon},${boundingBox.centerLat}`;
      }

      const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
        query
      )}&${biasParams}&${filterParams}&limit=5&lang=pt&apiKey=${apiKey}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        this.setState({
          suggestions: data.features,
          loading: false,
        });
      } else {
        this.setState({
          suggestions: [],
          loading: false,
        });
      }
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      this.setState({
        suggestions: [],
        loading: false,
      });
    }
  };

  handleSelectAddress = (feature) => {
    const { properties, geometry } = feature;
    const address = properties.formatted || properties.address_line1 || "";
    const [longitude, latitude] = geometry.coordinates;

    const selectedAddress = {
      address,
      latitude,
      longitude,
      properties,
    };

    this.setState({
      inputValue: address,
      showSuggestions: false,
      suggestions: [],
      selectedAddress,
    });

    // Validate address
    this.validateAddress(latitude, longitude);

    // Notify parent component
    if (this.props.onAddressSelect) {
      this.props.onAddressSelect(selectedAddress);
    }
  };

  validateAddress = (latitude, longitude) => {
    const { deliveryAreas } = this.state;

    if (deliveryAreas.length === 0) {
      this.setState({
        validationStatus: "invalid",
        validationMessage: "No delivery areas configured yet",
        scheduleMessage: "",
      });
      if (this.props.onValidationChange) {
        this.props.onValidationChange(false, null);
      }
      return;
    }

    // Create a Turf.js point
    const addressPoint = point([longitude, latitude]);

    // Check if point is inside any active delivery area
    let isValid = false;
    let matchedArea = null;
    let matchedAreaData = null;

    for (const area of deliveryAreas) {
      if (area.geojson && area.geojson.geometry) {
        try {
          const isInside = booleanPointInPolygon(addressPoint, area.geojson);
          if (isInside) {
            isValid = true;
            matchedArea = area.name;
            matchedAreaData = area;
            break;
          }
        } catch (error) {
          console.error("Error validating polygon:", error);
        }
      }
    }

    if (isValid) {
      // Verifica horário de funcionamento
      const businessHoursStatus = isDeliveryAreaOpen(matchedAreaData.businessHours);
      
      if (businessHoursStatus.isOpen) {
        // Área válida e aberta
        this.setState({
          validationStatus: "valid",
          validationMessage: `✓ Entregamos nesta região! (${matchedArea})`,
          scheduleMessage: businessHoursStatus.hasSchedule 
            ? `🕐 ${businessHoursStatus.message}`
            : "",
        });
        if (this.props.onValidationChange) {
          this.props.onValidationChange(true, matchedArea, { isOpen: true });
        }
      } else {
        // Área válida mas fechada
        this.setState({
          validationStatus: "closed",
          validationMessage: `📍 Entregamos nesta região (${matchedArea})`,
          scheduleMessage: `⏰ ${businessHoursStatus.message}`,
        });
        if (this.props.onValidationChange) {
          // Passa isOpen: false para indicar que está fechado
          this.props.onValidationChange(false, matchedArea, { 
            isOpen: false, 
            isClosed: true,
            scheduleMessage: businessHoursStatus.message 
          });
        }
      }
    } else {
      this.setState({
        validationStatus: "invalid",
        validationMessage:
          "✗ Desculpe, ainda não entregamos neste endereço. Entre em contato para mais informações.",
        scheduleMessage: "",
      });
      if (this.props.onValidationChange) {
        this.props.onValidationChange(false, null);
      }
    }
  };

  getValidationClass = () => {
    const { classes } = this.props;
    const { validationStatus } = this.state;
    
    switch (validationStatus) {
      case "valid":
        return classes.validAddress;
      case "closed":
        return classes.closedAddress;
      case "invalid":
      default:
        return classes.invalidAddress;
    }
  };

  getValidationIcon = () => {
    const { validationStatus } = this.state;
    
    switch (validationStatus) {
      case "valid":
        return <CheckCircleIcon fontSize="small" />;
      case "closed":
        return <ScheduleIcon fontSize="small" />;
      case "invalid":
      default:
        return <ErrorIcon fontSize="small" />;
    }
  };

  render() {
    const { classes, label, placeholder } = this.props;
    const {
      inputValue,
      suggestions,
      loading,
      showSuggestions,
      validationStatus,
      validationMessage,
      scheduleMessage,
    } = this.state;

    return (
      <div className={classes.root}>
        <TextField
          fullWidth
          label={label || "Endereço de Entrega"}
          placeholder={placeholder || "Digite seu endereço completo..."}
          value={inputValue}
          onChange={this.handleInputChange}
          variant="outlined"
          InputProps={{
            endAdornment: loading && <CircularProgress size={20} />,
          }}
        />

        {showSuggestions && suggestions.length > 0 && (
          <Paper className={classes.suggestionsPaper} elevation={3}>
            {suggestions.map((feature, index) => (
              <div
                key={index}
                className={classes.suggestionItem}
                onClick={() => this.handleSelectAddress(feature)}
              >
                <Typography variant="body2">
                  {feature.properties.formatted || feature.properties.address_line1}
                </Typography>
                {feature.properties.address_line2 && (
                  <Typography variant="caption" color="textSecondary">
                    {feature.properties.address_line2}
                  </Typography>
                )}
              </div>
            ))}
          </Paper>
        )}

        {validationStatus && (
          <Box
            className={`${classes.validationMessage} ${this.getValidationClass()}`}
          >
            <Box>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                {this.getValidationIcon()}
                <Typography variant="body2">{validationMessage}</Typography>
              </Box>
              {scheduleMessage && (
                <Typography variant="caption" className={classes.scheduleInfo}>
                  {scheduleMessage}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(AddressAutocomplete);
